import { givenNameOf } from './studentName';
import { normalizeForSearch } from './textSearch';

/**
 * Everything the enrolment pickers need in order to search and label a row —
 * deliberately narrower than `EnrollmentWithSubject` so a plain object works
 * in a test and any future list shape works at a call site.
 */
export interface SearchableEnrollment {
  student: { name: string; nickname: string | null };
}

/**
 * Given-name order, the client-side mirror of the DB sort: the enrolments
 * endpoint comes back `createdAt desc` with no `givenName` column, so the
 * pickers derive it and collate with `vi` (which puts Ă with A rather than
 * after Z). Returns a NEW array — callers memoise the original.
 */
export function sortByGivenName<T extends SearchableEnrollment>(options: T[]): T[] {
  return [...options].sort((a, b) =>
    givenNameOf(a.student.name).localeCompare(givenNameOf(b.student.name), 'vi'));
}

/**
 * Diacritic- and case-insensitive substring match on name OR nickname, with
 * both sides folded the same way so "thien an" and "Thiên" both find Thiên Ân.
 * An empty query returns the list untouched (identity, not a copy) — "no
 * search" is not "no matches".
 */
export function filterByName<T extends SearchableEnrollment>(options: T[], query: string): T[] {
  const needle = normalizeForSearch(query).trim();
  if (!needle) return options;
  return options.filter((e) =>
    normalizeForSearch(e.student.name).includes(needle)
    || (!!e.student.nickname && normalizeForSearch(e.student.nickname).includes(needle)));
}

/**
 * "Tên đầy đủ (tên ở nhà)" — the label every enrolment picker shows. Same
 * disambiguation pattern as `ClientCombobox`'s phone-in-brackets (THI-82); the
 * nickname is often the only name the teacher recognises a child by.
 */
export function enrollmentDisplayName(e: SearchableEnrollment): string {
  return e.student.nickname ? `${e.student.name} (${e.student.nickname})` : e.student.name;
}
